import { FC, memo, useCallback, useState } from "react";
import { Alert, Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import { CreateCompanyDialog } from "../../components/CreateCompanyDialog";
import { DataGrid } from "../../../ui/components/DataGrid";
import { FunctionViewField } from "../../../ui/components/viewField/FunctionViewField";
import { ListContainer } from "../../../ui/containers/ListContainer";
import { ImageFile } from "../../../ui/components/FileBlock/styles";
import { getCompanyStatusIcon, companyStatusesModel } from "../../utils";
import { IntegrationConfigDialog } from "../../components/IntegrationConfigDialog";
import {
  FourEyesMode,
  FourEyesModeView,
  getApplicationsList,
  getApplicationsListNew,
} from "../../network";
import { ICompanyActionModeType, TCompany } from "../../models";
import { TApp } from "../../../app/models";
import { CompanyRole } from "../../../../auth/AuthProvider";
import { IUserCompanyRoleObject } from "../../../verificationRequest/network";
import { ActionType } from "../../../../models/ActionType";

interface ICompanyListProps {
  companies: any;
  companyRoleObj: IUserCompanyRoleObject;
  isSuperAdmin: boolean;
  reload: () => void;
  error: Error | null;
}

export const CompanyListScreen: FC<ICompanyListProps> = memo(
  ({ companies, reload, error, companyRoleObj, isSuperAdmin }) => {
    const [isActionOpen, setIsActionOpen] = useState(false);
    const [actionMode, setActionMode] = useState<ICompanyActionModeType>("");
    const [selectedCompany, setSelectedCompany] = useState<TCompany | null>(
      null
    );
    const [isIntegrationOpen, setIsIntegrationOpen] = useState(false);
    const [appList, setAppList] = useState<TApp[] | []>([]);

    const handleOpenAction = useCallback(
      (mode: ICompanyActionModeType, company?: any) => {
        setActionMode(mode);
        if (company) {
          setSelectedCompany(company);
        }
        setIsActionOpen(true);
      },
      [setIsActionOpen]
    );

    const handleCloseCreate = useCallback(
      (success: boolean) => {
        setIsActionOpen(false);
        setSelectedCompany(null);
        if (success) {
          reload();
        }
      },
      [setIsActionOpen, reload]
    );

    const integrationConfigHandler = async (record: TCompany) => {
      setSelectedCompany(record);
      getApplicationsListNew(record.id)
        .then((appList) => setAppList(appList))
        .then(() => setIsIntegrationOpen(true))
        .catch((e) =>
          e.message === "Not Found"
            ? getApplicationsList()
                .then((appList) => setAppList(appList))
                .then(() => setIsIntegrationOpen(true))
            : null
        );
    };

    return (
      <>
        <Stack direction='row' spacing={2} justifyContent='end'>
          <Button
            onClick={() => handleOpenAction(ActionType.CREATE)}
            variant='contained'
            disabled={!isSuperAdmin}
          >
            Create company
          </Button>
        </Stack>
        <ListContainer data={companies?.items}>
          <DataGrid>
            <FunctionViewField
              label='Name'
              source='name'
              render={(record: TCompany) => (
                <Box>
                  <Typography variant='h6'>{record.name}</Typography>
                </Box>
              )}
            />
            <FunctionViewField
              label='ID'
              source='id'
              render={(record: TCompany) => (
                <Box>
                  <Typography variant='body2'>{record.id}</Typography>
                </Box>
              )}
            />
            <FunctionViewField
              label='Purpose'
              source='purpose'
              render={(record: TCompany) => (
                <Box>
                  <Typography variant='body2'>{record.purpose}</Typography>
                </Box>
              )}
            />
            <FunctionViewField
              label='4-Eyes'
              source='4-eyes'
              render={(record: TCompany) => (
                <Box>
                  <Typography variant='body2'>
                    {record.fourEyesMode !== FourEyesMode.DISABLED
                      ? FourEyesModeView[record.fourEyesMode]
                      : ""}
                  </Typography>
                </Box>
              )}
            />
            <FunctionViewField
              label='Status'
              source='status'
              render={(record: TCompany) => (
                <Stack
                  direction='row'
                  justifyContent='flex-start'
                  alignItems='center'
                  spacing={1}
                  minWidth={"200px"}
                >
                  <Box
                    sx={{
                      padding: "5px 10px",
                      background:
                        companyStatusesModel[String(record.isActive)]?.bgColor,
                      borderRadius: "6px",
                    }}
                  >
                    <Stack
                      direction='row'
                      justifyContent='center'
                      alignItems='center'
                      position='relative'
                    >
                      <Box position='absolute' top='0' left='0'>
                        {getCompanyStatusIcon(record.isActive)}
                      </Box>
                      <Typography
                        variant='body2'
                        sx={{
                          paddingLeft: "25px",
                          fontWeight: "700",
                          color:
                            companyStatusesModel[String(record.isActive)]
                              ?.textColor,
                        }}
                      >
                        {companyStatusesModel[String(record.isActive)]?.name}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              )}
            />
            <FunctionViewField
              label='Logo'
              source='picture'
              render={(record: TCompany) => (
                <Box>
                  <ImageFile src={record.picture} />
                </Box>
              )}
            />
            <FunctionViewField
              label='Actions'
              source='actions'
              render={(record: TCompany) => (
                <Stack direction='row'>
                  <Button
                    disabled={
                      !(
                        companyRoleObj[record.id] === CompanyRole.ADMIN ||
                        isSuperAdmin
                      )
                    }
                    onClick={() => handleOpenAction(ActionType.EDIT, record)}
                  >
                    Edit
                  </Button>
                  <Tooltip
                    arrow
                    classes={{ arrow: "arrow", tooltip: "tooltip" }}
                    placement={"top-end"}
                    title={!record.isActive ? "Company must be active" : ""}
                  >
                    <span>
                      <Button
                        disabled={
                          !record.isActive ||
                          !(
                            companyRoleObj[record.id] === CompanyRole.ADMIN ||
                            isSuperAdmin
                          )
                        }
                        onClick={() => integrationConfigHandler(record)}
                      >
                        Integration Configuration
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    disabled={!isSuperAdmin}
                    onClick={() => handleOpenAction("delete", record)}
                  >
                    Delete
                  </Button>
                </Stack>
              )}
            />
          </DataGrid>
        </ListContainer>
        {error !== null && <Alert severity='error'>{error.toString()}</Alert>}
        <CreateCompanyDialog
          selectedCompany={selectedCompany}
          actionMode={actionMode}
          open={isActionOpen}
          onClose={handleCloseCreate}
        />
        <IntegrationConfigDialog
          selectedCompany={selectedCompany}
          appList={appList}
          setAppList={setAppList}
          open={isIntegrationOpen}
          onClose={() => setIsIntegrationOpen(false)}
        />
      </>
    );
  }
);
